/**
 * One Saturday per month for home tasks.
 * A day holds at most 4 open tasks. A month holds at most 8.
 * Lower-priority overflow moves to the next Saturday, then the next month.
 * Weekly chores and legal dates stay on their own day.
 * CommonJS only. No object spread.
 */

const { localDateString, parseLocalDate } = require('../utils/dates');

const MONTH_CAP = 8;
const DAY_CAP = 4;

function priorityRank(task) {
  const raw = task && (task.priority || (task.task && task.task.priority));
  const value = String(raw || 'medium').trim().toLowerCase();
  if (value === 'high' || value === 'critical') return 0;
  if (value === 'low') return 2;
  return 1;
}

function checklistSaturday(year, monthIndex, today) {
  const first = new Date(year, monthIndex, 1);
  const offset = (6 - first.getDay() + 7) % 7;
  let saturday = new Date(year, monthIndex, 1 + offset);
  const todayDate = today ? parseLocalDate(today) : null;
  if (todayDate) {
    const end = new Date(year, monthIndex + 1, 0);
    while (saturday < todayDate && saturday <= end) {
      const next = new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate() + 7);
      if (next.getMonth() !== monthIndex) break;
      saturday = next;
    }
  }
  return localDateString(saturday);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function isSaturdayKey(value) {
  const date = parseLocalDate(value);
  return Boolean(date && date.getDay() === 6);
}

function isTuneUpTask(task) {
  const key = task && task.conditions && task.conditions.key;
  if (key && /^fl_hvac_tune_/.test(String(key))) return true;
  const title = String((task && (task.title || task.task_name)) || '').toLowerCase();
  return /tune-up|tune up|cooling tune/.test(title) && /ac|a\/c|hvac|air condition/.test(title);
}

function isFixedLegalTask(task) {
  if (!task) return false;
  if (task.region_task_id) return true;
  const title = String(task.title || task.task_name || '').toLowerCase();
  return /insurance|warranty|flood|\brenew|property tax|trim notice|homestead/.test(title);
}

function canBundleTask(task) {
  if (!task || isFixedLegalTask(task)) return false;
  const freq = String(task.frequency || '').trim().toLowerCase();
  if (freq === 'daily' || freq === 'weekly' || freq === 'every week' || freq === 'bi-weekly' || freq === 'biweekly') {
    return false;
  }
  return true;
}

function nextMaySaturday(today) {
  const base = parseLocalDate(today) || new Date();
  const todayKey = localDateString(base);
  let year = base.getFullYear();
  let saturday = checklistSaturday(year, 4, null);
  if (saturday < todayKey) saturday = checklistSaturday(year + 1, 4, null);
  return saturday;
}

function taskKey(task, index) {
  if (task && task.id) return String(task.id);
  const key = task && task.conditions && task.conditions.key;
  if (key) return String(key);
  return 'row-' + index;
}

function savedOpenDate(task) {
  if (!task || task.completed || task.skipped_at || task.dismissed_at || task.dismiss_reason) return '';
  const snoozed = String(task.snoozed_until || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(snoozed)) return snoozed;
  const due = String(task.due_date || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(due)) return due;
  return '';
}

function addDaysKey(value, days) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return localDateString(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
}

/**
 * planned: [{ task, dates }]
 * existing: this homeowner's saved open tasks. Optional.
 * Returns a new list. Tune-ups in the horizon share the next May Saturday.
 * Other movable dates land on that month's checklist Saturday. A full day
 * uses the next Saturday in the month. A full month uses the next month.
 */
function bundlePlanned(planned, today, existing) {
  const may = nextMaySaturday(today);
  const entries = [];
  (planned || []).forEach((row, rowIndex) => {
    if (!row) return;
    const dates = isTuneUpTask(row.task) ? [may] : (row.dates || []).slice();
    dates.forEach((date, dateIndex) => {
      entries.push({
        rowIndex,
        dateIndex,
        date: String(date).slice(0, 10),
        task: row.task,
        priority: isTuneUpTask(row.task) ? -1 : priorityRank(row.task),
        movable: canBundleTask(row.task) || isTuneUpTask(row.task),
        identity: taskKey(row.task, rowIndex),
      });
    });
  });

  const monthCounts = {};
  const dayCounts = {};
  const placedIdentity = {};
  (existing || []).forEach((task) => {
    const date = savedOpenDate(task);
    if (!date) return;
    const month = date.slice(0, 7);
    monthCounts[month] = (monthCounts[month] || 0) + 1;
    dayCounts[date] = (dayCounts[date] || 0) + 1;
  });
  const ordered = entries.filter((entry) => entry.movable).slice().sort((a, b) => (
    a.priority - b.priority || a.date.localeCompare(b.date) || a.identity.localeCompare(b.identity)
  ));

  ordered.forEach((entry) => {
    let cursor = parseLocalDate(entry.date) || parseLocalDate(today) || new Date();
    let chosen = entry.date;
    for (let hop = 0; hop < 8; hop += 1) {
      const saturday = checklistSaturday(cursor.getFullYear(), cursor.getMonth(), today);
      const month = saturday.slice(0, 7);
      const stamp = entry.identity + '|' + month;
      const full = (monthCounts[month] || 0) >= MONTH_CAP;
      if (!full && !placedIdentity[stamp]) {
        let day = saturday;
        let guard = 0;
        while (day && day.slice(0, 7) === month && guard < 6) {
          if ((dayCounts[day] || 0) < DAY_CAP) {
            chosen = day;
            monthCounts[month] = (monthCounts[month] || 0) + 1;
            dayCounts[day] = (dayCounts[day] || 0) + 1;
            placedIdentity[stamp] = true;
            break;
          }
          day = addDaysKey(day, 7);
          guard += 1;
        }
        if (placedIdentity[stamp]) break;
      }
      cursor = addMonths(cursor, 1);
    }
    entry.placed = chosen;
  });

  const byRow = {};
  entries.forEach((entry) => {
    if (!byRow[entry.rowIndex]) byRow[entry.rowIndex] = [];
    byRow[entry.rowIndex].push(entry.placed || entry.date);
  });

  return (planned || []).map((row, rowIndex) => {
    if (!row) return row;
    const dates = byRow[rowIndex];
    if (!dates) return row;
    return Object.assign({}, row, { dates: dates.slice().sort() });
  });
}

function monthChecklistTitle(monthIndex) {
  if (monthIndex === 4) return 'Hurricane prep';
  if (monthIndex === 9) return 'Fall checklist';
  return 'This month';
}

function isSnoozedTask(task, today) {
  if (!task || !task.snoozed_until) return false;
  const until = String(task.snoozed_until).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(until)) return false;
  return until >= localDateString(today || new Date());
}

function checklistDateLabel(value) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function monthChecklist(tasks, today) {
  const base = parseLocalDate(today) || new Date();
  const startKey = localDateString(base).slice(0, 7);
  const open = (tasks || []).filter((task) => (
    task
    && !task.completed
    && !isSnoozedTask(task, base)
    && String(task.due_date || '').slice(0, 10) >= localDateString(base)
  ));
  const months = {};
  open.forEach((task) => {
    const due = String(task.due_date).slice(0, 10);
    const month = due.slice(0, 7);
    if (!months[month]) months[month] = [];
    months[month].push(task);
  });
  const keys = Object.keys(months).sort();
  const month = keys.find((key) => key >= startKey) || keys[0] || null;
  if (!month) {
    return { title: monthChecklistTitle(base.getMonth()), month: startKey, date: checklistSaturday(base.getFullYear(), base.getMonth(), base), items: [] };
  }
  const parts = month.split('-');
  const year = Number(parts[0]);
  const monthIndex = Number(parts[1]) - 1;
  const items = months[month].slice().sort((a, b) => (
    priorityRank(a.task || a) - priorityRank(b.task || b)
    || String(a.due_date).localeCompare(String(b.due_date))
  ));
  return {
    title: monthChecklistTitle(monthIndex),
    month,
    date: checklistSaturday(year, monthIndex, base),
    items,
  };
}

module.exports = {
  MONTH_CAP,
  DAY_CAP,
  priorityRank,
  checklistSaturday,
  isSaturdayKey,
  isTuneUpTask,
  isFixedLegalTask,
  canBundleTask,
  nextMaySaturday,
  bundlePlanned,
  monthChecklistTitle,
  monthChecklist,
  isSnoozedTask,
  checklistDateLabel,
};
